// Chart/Accent palette — must stay in sync with tailwind.config.js
// (colors.primary/cyan/orange/violet/rose). Order matches the design
// system's Chart/Accent token table: Blue/Primary, Cyan, Orange, Violet, Rosé.
export const CHART_ACCENT = {
  primary: '#3E54B8', // primary-600
  cyan: '#22CCEE', // cyan-400
  orange: '#F38744', // orange-400
  violet: '#A48AFB', // violet-400
  rose: '#FD6F8E', // rose-400
} as const

export const CHART_COLORS = [
  CHART_ACCENT.primary,
  CHART_ACCENT.cyan,
  CHART_ACCENT.orange,
  CHART_ACCENT.violet,
  CHART_ACCENT.rose,
]

export const SEMANTIC_COLORS = {
  positive: '#027A48',
  negative: '#B42318',
} as const

function isTooCloseToWhite(hex: string): boolean {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000
  return brightness > 235
}

type ColorableSite = { url: string; is_mine?: boolean; siteName?: string }

// Display order only (not color anymore, see getPersistentColorsBySiteId
// below): the user's own tracked URL first, then everyone else
// alphabetically by name. Used for the dropdown/legend list order in both
// PriceHistoryChart and the Daily View table.
export function orderSitesForDisplay<T extends ColorableSite>(
  sites: T[] | undefined,
): T[] {
  return [...(sites ?? [])].sort((a, b) => {
    const mineDiff = Number(!!b.is_mine) - Number(!!a.is_mine)
    if (mineDiff !== 0) return mineDiff
    return (a.siteName ?? a.url).localeCompare(b.siteName ?? b.url)
  })
}

function getSeriesColor(index: number): string {
  if (index < CHART_COLORS.length && !isTooCloseToWhite(CHART_COLORS[index])) {
    return CHART_COLORS[index]
  }
  // Fixed at 65% saturation / 50% lightness — structurally can never
  // approach white (which needs lightness near 100%) regardless of hue.
  const hue = (index * 47) % 360
  return `hsl(${hue}, 65%, 50%)`
}

const SITE_COLORS_STORAGE_KEY = 'prisync_site_colors_v1'

function loadPersistedSiteColors(): Record<number, string> {
  try {
    const raw = window.localStorage.getItem(SITE_COLORS_STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function savePersistedSiteColors(colors: Record<number, string>): void {
  try {
    window.localStorage.setItem(SITE_COLORS_STORAGE_KEY, JSON.stringify(colors))
  } catch {
    // Storage disabled/full — colors just won't survive a reload, still
    // fine for the current session.
  }
}

// A competitor's color is assigned once, the first time its site_id is
// ever seen, and persisted in localStorage — so it never changes because
// another competitor got added/removed or the display order shifted, and
// it stays the same across page reloads and across different products.
export function getPersistentColorsBySiteId(
  siteIds: number[],
): Map<number, string> {
  const persisted = loadPersistedSiteColors()
  let changed = false

  siteIds.forEach((siteId) => {
    if (persisted[siteId] === undefined) {
      persisted[siteId] = getSeriesColor(Object.keys(persisted).length)
      changed = true
    }
  })

  if (changed) savePersistedSiteColors(persisted)

  return new Map(siteIds.map((siteId) => [siteId, persisted[siteId]]))
}
